import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import type { Lead, Priority } from "@/mocks/leadsData";
import { stageBadgeColors, priorityDotColors } from "@/mocks/leadsData";
import { useToast } from "@/components/base/Toast";
import { fetchApi } from "@/services/apiService";
import type { StageConfigItem } from "./PipelineKanbanBoard";

type Props = {
  deals: Lead[];
  stageConfig?: StageConfigItem[];
  onView?: (deal: Lead) => void;
  onStageChange?: (dealId: number, newStage: string) => void;
  onAddDeal?: (stage?: string) => void;
};

type SortKey = "name" | "value" | "stage" | "days" | "priority";
type SortDir = "asc" | "desc";

const defaultStages: string[] = [
  "New Inquiry",
  "Site Visit Scheduled",
  "Site Visit Done",
  "Proposal Sent",
  "Negotiation",
  "Booking Done",
  "Lost",
];

const priorityOrder: Record<string, number> = {
  High: 0,
  Medium: 1,
  Low: 2,
};

const stageColorDot: Record<string, string> = {
  "New Inquiry": "bg-gray-400",
  "Site Visit Scheduled": "bg-blue-500",
  "Site Visit Done": "bg-indigo-500",
  "Proposal Sent": "bg-amber-500",
  "Negotiation": "bg-orange-500",
  "Booking Done": "bg-green-500",
  "Lost": "bg-red-400",
};

export function PipelineTableView({ deals = [], stageConfig, onView, onStageChange, onAddDeal }: Props) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [stageDropdownDealId, setStageDropdownDealId] = useState<number | null>(null);
  const [availableStages, setAvailableStages] = useState<string[]>(defaultStages);
  const [confirmStageChange, setConfirmStageChange] = useState<{
    dealId: number;
    dealName: string;
    newStage: string;
  } | null>(null);

  useEffect(() => {
    if (stageConfig && stageConfig.length > 0) {
      setAvailableStages(stageConfig.map((s) => s.stage));
      return;
    }

    fetchApi<any[]>('/config/pipeline-stages/get-all', undefined, [])
      .then((res) => {
        const items = Array.isArray(res) ? res : (res as any)?.data || [];
        if (items.length > 0) {
          const names = items.map((s: any) => s.name).filter(Boolean);
          if (names.length > 0) setAvailableStages(names);
        }
      })
      .catch(() => {});
  }, [stageConfig]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const parseValueNumber = (valStr: string | undefined): number => {
    if (!valStr) return 0;
    const num = parseFloat(valStr.replace(/[₹,]/g, ""));
    return isNaN(num) ? 0 : num;
  };

  const getDaysInStage = (deal: Lead): number => {
    if (deal.lastActivity) {
      const parsed = Date.parse(deal.lastActivity);
      if (!isNaN(parsed)) {
        const diffMs = Date.now() - parsed;
        return Math.max(1, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
      }
      if (deal.lastActivity.includes("hour") || deal.lastActivity.includes("min") || deal.lastActivity.includes("Just")) {
        return 1;
      }
      const match = deal.lastActivity.match(/(\d+)\s*day/);
      if (match) return parseInt(match[1], 10);
    }
    return 1;
  };

  const sortedDeals = [...deals].sort((a, b) => {
    if (!sortKey) return 0;
    let cmp = 0;
    const numA = parseValueNumber(a.value);
    const numB = parseValueNumber(b.value);
    const daysA = getDaysInStage(a);
    const daysB = getDaysInStage(b);

    switch (sortKey) {
      case "name":
        cmp = (a.company || a.name || "").localeCompare(b.company || b.name || "");
        break;
      case "value":
        cmp = numA - numB;
        break;
      case "stage": {
        const idxA = availableStages.indexOf(a.stage);
        const idxB = availableStages.indexOf(b.stage);
        cmp = (idxA === -1 ? 99 : idxA) - (idxB === -1 ? 99 : idxB);
        break;
      }
      case "days":
        cmp = daysA - daysB;
        break;
      case "priority":
        cmp = (priorityOrder[a.priority] ?? 99) - (priorityOrder[b.priority] ?? 99);
        break;
    }
    return sortDir === "asc" ? cmp : -cmp;
  });

  const getInitials = (name: string | undefined) => {
    if (!name) return "U";
    return name
      .split(" ")
      .filter(Boolean)
      .map((n) => n[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "U";
  };

  const handleRowClick = (deal: Lead) => {
    if (onView) {
      onView(deal);
    } else {
      navigate(`/leads/${deal.id}`);
    }
  };

  const SortIcon = ({ column }: { column: SortKey }) => {
    if (sortKey !== column) return <i className="ri-arrow-up-down-line text-[10px] text-gray-300 ml-1" />;
    return (
      <i
        className={`text-[10px] ml-1 text-indigo-500 ${
          sortDir === "asc" ? "ri-sort-asc" : "ri-sort-desc"
        }`}
      />
    );
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50/80">
              <th
                className="px-4 py-3.5 text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap cursor-pointer select-none hover:text-gray-900"
                onClick={() => handleSort("name")}
              >
                <span className="inline-flex items-center">Company / Lead <SortIcon column="name" /></span>
              </th>
              <th
                className="px-4 py-3.5 text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap cursor-pointer select-none hover:text-gray-900"
                onClick={() => handleSort("stage")}
              >
                <span className="inline-flex items-center">Stage <SortIcon column="stage" /></span>
              </th>
              <th
                className="px-4 py-3.5 text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap cursor-pointer select-none hover:text-gray-900"
                onClick={() => handleSort("value")}
              >
                <span className="inline-flex items-center">Value <SortIcon column="value" /></span>
              </th>
              <th
                className="px-4 py-3.5 text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap cursor-pointer select-none hover:text-gray-900"
                onClick={() => handleSort("priority")}
              >
                <span className="inline-flex items-center">Priority <SortIcon column="priority" /></span>
              </th>
              <th className="px-4 py-3.5 text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                Assigned To
              </th>
              <th
                className="px-4 py-3.5 text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap cursor-pointer select-none hover:text-gray-900"
                onClick={() => handleSort("days")}
              >
                <span className="inline-flex items-center">Days in Stage <SortIcon column="days" /></span>
              </th>
              <th className="px-4 py-3.5 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sortedDeals.map((deal) => {
              const daysInStage = getDaysInStage(deal);
              const assignedName = deal.assigned || "Unassigned";

              return (
                <tr
                  key={deal.id}
                  className="hover:bg-indigo-50/30 transition-colors cursor-pointer"
                  onClick={() => handleRowClick(deal)}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-bold text-indigo-700 flex-shrink-0">
                        {getInitials(deal.name)}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 truncate hover:text-indigo-600 transition-colors">
                          {deal.company || deal.name}
                        </p>
                        <p className="text-xs text-gray-500 truncate">{deal.name}</p>
                      </div>
                    </div>
                  </td>

                  <td className="px-4 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setStageDropdownDealId(stageDropdownDealId === deal.id ? null : deal.id)}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${
                          (stageBadgeColors as any)[deal.stage] || "bg-gray-100 text-gray-700"
                        } hover:ring-2 hover:ring-indigo-200 transition-all cursor-pointer`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${stageColorDot[deal.stage] || "bg-gray-400"}`} />
                        {deal.stage || "New Inquiry"}
                        <i className="ri-arrow-down-s-line text-[10px] opacity-60" />
                      </button>

                      {stageDropdownDealId === deal.id && (
                        <>
                          <div className="fixed inset-0 z-30" onClick={() => setStageDropdownDealId(null)} />
                          <div className="absolute left-0 top-full mt-1 bg-white rounded-xl border border-gray-200 shadow-xl z-40 py-1 w-52 max-h-60 overflow-y-auto">
                            {availableStages.map((s) => (
                              <button
                                key={s}
                                type="button"
                                onClick={() => {
                                  setStageDropdownDealId(null);
                                  if (s === deal.stage) return;
                                  if (s === "Booking Done" || s === "Lost" || s === "Closed Won" || s === "Closed Lost") {
                                    setConfirmStageChange({ dealId: deal.id, dealName: deal.name, newStage: s });
                                  } else {
                                    onStageChange?.(deal.id, s);
                                  }
                                }}
                                className={`w-full flex items-center gap-2.5 px-3.5 py-2 text-xs transition-colors whitespace-nowrap cursor-pointer text-left ${
                                  s === deal.stage ? "bg-indigo-50 text-indigo-700 font-semibold" : "text-gray-700 hover:bg-gray-50"
                                }`}
                              >
                                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${stageColorDot[s] || "bg-gray-400"}`} />
                                {s}
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </td>

                  <td className="px-4 py-3 whitespace-nowrap font-bold text-indigo-600">
                    {deal.value || "₹0"}
                  </td>

                  <td className="px-4 py-3 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center gap-1.5 text-[11px] font-semibold ${
                        deal.priority === "High"
                          ? "text-red-600"
                          : deal.priority === "Medium"
                          ? "text-amber-600"
                          : "text-green-600"
                      }`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${priorityDotColors[deal.priority as Priority] || "bg-gray-400"}`} />
                      {deal.priority || "Medium"}
                    </span>
                  </td>

                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center text-[10px] font-bold text-indigo-700 flex-shrink-0">
                        {getInitials(assignedName)}
                      </div>
                      <span className="text-xs text-gray-700 font-medium">{assignedName}</span>
                    </div>
                  </td>

                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-600">
                      {daysInStage}d
                    </span>
                  </td>

                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRowClick(deal);
                        }}
                        className="p-1.5 rounded-lg hover:bg-indigo-50 text-gray-400 hover:text-indigo-600 transition-colors"
                        title="View Lead Details"
                      >
                        <i className="ri-eye-line text-base" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/leads/${deal.id}`);
                        }}
                        className="p-1.5 rounded-lg hover:bg-indigo-50 text-gray-400 hover:text-indigo-600 transition-colors"
                        title="Edit / Open Lead"
                      >
                        <i className="ri-edit-line text-base" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}

            {sortedDeals.length === 0 && (
              <tr>
                <td colSpan={7} className="px-5 py-16 text-center">
                  <i className="ri-inbox-line text-3xl text-gray-300 mb-2 block" />
                  <p className="text-sm font-medium text-gray-500">No deals found in pipeline</p>
                  {onAddDeal && (
                    <button
                      type="button"
                      onClick={() => onAddDeal()}
                      className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-semibold hover:bg-indigo-700 transition-colors cursor-pointer"
                    >
                      <i className="ri-add-line" /> Add Deal
                    </button>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Stage change confirmation for Booking Done / Lost */}
      {confirmStageChange && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl border border-gray-200 shadow-xl p-6 max-w-sm w-full mx-4 z-10 animate-scale-in">
            {confirmStageChange.newStage === "Booking Done" || confirmStageChange.newStage === "Closed Won" ? (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-double-line text-green-600 text-lg" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Mark as Won / Booking Done?</h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Move <strong>{confirmStageChange.dealName}</strong> to {confirmStageChange.newStage}?
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 justify-end">
                  <button
                    type="button"
                    onClick={() => setConfirmStageChange(null)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      onStageChange?.(confirmStageChange.dealId, confirmStageChange.newStage);
                      setConfirmStageChange(null);
                    }}
                    className="px-4 py-2 text-sm font-semibold text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Confirm Won
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-close-line text-red-600 text-lg" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Mark as Lost?</h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Move <strong>{confirmStageChange.dealName}</strong> to {confirmStageChange.newStage}?
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 justify-end">
                  <button
                    type="button"
                    onClick={() => setConfirmStageChange(null)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      onStageChange?.(confirmStageChange.dealId, confirmStageChange.newStage);
                      setConfirmStageChange(null);
                    }}
                    className="px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Mark as Lost
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}